import { Button, Center, Loader, Stack } from "@mantine/core";
import { ErrorAlert } from "@mountaya/studio-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { AuthPageHeader } from "../auth/components/AuthPageHeader";
import { ory } from "../auth/rules/ory";

/**
 * errorSearchSchema validates the /error search params. id is the Ory error
 * container id (required) that getFlowError resolves.
 */
const errorSearchSchema = z.object({
  id: z.string(),
});

/**
 * flowErrorBodySchema parses the untrusted FlowError.error object (typed
 * `object` in the SDK) into the fields worth rendering. All optional, with safe
 * fallbacks, so a sparse error payload still renders.
 */
const flowErrorBodySchema = z
  .object({
    id: z.string().optional(),
    message: z.string().optional(),
    reason: z.string().optional(),
    status: z.string().optional(),
  })
  .catch({});

/**
 * Route is /error: the Ory error page. It resolves the error container by id and
 * renders its message/reason. Used as the fallback for disabled/unrecoverable
 * engine errors (the hook navigates here).
 */
export const Route = createFileRoute("/_auth/error")({
  component: ErrorRoute,
  validateSearch: errorSearchSchema,
});

function ErrorRoute() {
  const { t } = useTranslation("iam");
  const { id } = Route.useSearch();
  const [message, setMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void ory
      .getFlowError({ id })
      .then((flowError) => {
        if (!active) {
          return;
        }

        const parsed = flowErrorBodySchema.parse(flowError.error);
        setMessage(parsed.message ?? parsed.reason ?? t("error.unexpected"));
      })
      .catch(() => {
        if (active) {
          setMessage(t("error.loadFailed"));
        }
      })
      .finally(() => {
        if (active) {
          setIsLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id, t]);

  return (
    <Stack gap="lg">
      <AuthPageHeader subtitle={t("error.subtitle")} title={t("error.title")} />

      {isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <ErrorAlert>{message}</ErrorAlert>
      )}

      <Button component={Link} fullWidth to="/login" variant="light">
        {t("error.backToSignIn")}
      </Button>
    </Stack>
  );
}
